import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { PageTransition, FadeIn, StaggerContainer, StaggerItem } from '../components/layout';
import { Button } from '../components/ui/button';
import { ChevronRight, ShoppingBag, Trash2, Edit2, ArrowRight, AlertCircle, Truck, Gift } from 'lucide-react';

// Shipping constants (should match backend)
const SHIPPING_FEE = 3.50;
const FREE_SHIPPING_THRESHOLD = 60.0;

const shapeLabels = {
  square: 'Carré', round: 'Rond', almond: 'Amande',
  stiletto: 'Stiletto', coffin: 'Cercueil/Ballerina', oval: 'Ovale'
};

const lengthLabels = {
  short: 'Court', medium: 'Moyen', long: 'Long', extra_long: 'Extra Long'
};

// Helper to calculate shipping fee (mirrors backend logic)
const calculateShippingFee = (subtotal, hasPressOnNails) => {
  // No shipping for gift cards only
  if (!hasPressOnNails) return 0;
  // Free shipping above threshold
  if (subtotal >= FREE_SHIPPING_THRESHOLD) return 0;
  return SHIPPING_FEE;
};

export default function PanierPage() {
  const { items, removeItem, clearCart, getTotal } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [removingId, setRemovingId] = useState(null);
  
  // Check if cart contains physical products
  const hasPressOnNails = items.some(item => item.type !== 'gift_card');
  
  // Calculate shipping
  const subtotal = getTotal();
  const deliveryFee = calculateShippingFee(subtotal, hasPressOnNails);
  const totalWithShipping = subtotal + deliveryFee;
  const isFreeShipping = deliveryFee === 0;
  const amountForFreeShipping = hasPressOnNails ? Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal) : 0;

  const handleRemove = (id) => {
    setRemovingId(id);
    setTimeout(() => {
      removeItem(id);
      setRemovingId(null);
    }, 300);
  };

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/panier' } } });
      return;
    }
    // TODO: Implement Stripe checkout
    navigate('/checkout');
  };

  return (
    <PageTransition>
      {/* Hero */}
      <section className="pt-32 pb-8 px-6 bg-gradient-to-b from-[var(--champagne)]/30 to-white">
        <div className="max-w-[800px] mx-auto text-center">
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="font-['Cormorant_Garamond'] text-4xl md:text-5xl font-light mb-4 text-[var(--charcoal)]"
          >
            Mon Panier
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="text-[var(--charcoal)]/60"
          >
            {items.length} article{items.length > 1 ? 's' : ''} dans votre panier
          </motion.p>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="bg-white py-4 border-b border-[var(--champagne)]">
        <div className="max-w-[1400px] mx-auto px-6">
          <nav className="flex items-center gap-2 text-sm text-[var(--charcoal)]/60">
            <Link to="/" className="hover:text-[var(--gold)]">Accueil</Link>
            <ChevronRight size={14} />
            <span className="text-[var(--charcoal)]">Panier</span>
          </nav>
        </div>
      </div>

      {/* Content */}
      <section className="py-12 px-6" data-testid="panier-content">
        <div className="max-w-[1000px] mx-auto">
          {items.length === 0 ? (
            <FadeIn>
              <div className="text-center py-16 bg-white border border-[var(--champagne)]">
                <ShoppingBag size={64} className="mx-auto mb-6 text-[var(--charcoal)]/20" />
                <h2 className="font-['Cormorant_Garamond'] text-2xl mb-4 text-[var(--charcoal)]">
                  Votre panier est vide
                </h2>
                <p className="text-[var(--charcoal)]/60 mb-8">
                  Découvrez nos Press On Nails personnalisés
                </p>
                <Link
                  to="/press-on-nails"
                  className="inline-block px-8 py-4 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors"
                  data-testid="btn-shop-now"
                >
                  Voir les Press On Nails
                </Link>
              </div>
            </FadeIn>
          ) : (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Cart Items */}
              <div className="lg:col-span-2">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="font-['Cormorant_Garamond'] text-xl text-[var(--charcoal)]">
                    Vos articles
                  </h2>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearCart}
                    className="text-red-500 hover:text-red-700"
                    data-testid="btn-clear-cart"
                  >
                    <Trash2 size={14} className="mr-2" />
                    Vider le panier
                  </Button>
                </div>

                <AnimatePresence>
                  <StaggerContainer className="space-y-4">
                    {items.map((item, index) => (
                      <motion.div
                        key={item.id}
                        initial={{ opacity: 1, x: 0 }}
                        animate={{ 
                          opacity: removingId === item.id ? 0 : 1,
                          x: removingId === item.id ? -100 : 0
                        }}
                        exit={{ opacity: 0, x: -100 }}
                        transition={{ duration: 0.3 }}
                      >
                        <StaggerItem>
                          <div
                            className="bg-white p-6 border border-[var(--champagne)] hover:border-[var(--gold)]/30 transition-colors"
                            data-testid={`cart-item-${index}`}
                          >
                            {item.type === 'formation_product' ? (
                              <div className="flex gap-4">
                                <div className="w-24 h-24 shrink-0 bg-[var(--champagne)]/30 flex items-center justify-center">
                                  <ShoppingBag size={32} className="text-[var(--gold)]" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-['Cormorant_Garamond'] text-lg mb-2 text-[var(--charcoal)]">
                                    {item.product_name || 'Produit formation'}
                                  </h3>
                                  <p className="text-sm text-[var(--charcoal)]/70">
                                    Produit physique soumis aux règles de livraison.
                                  </p>
                                </div>
                                <div className="text-right">
                                  <p className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)] mb-4">
                                    {item.totalPrice}€
                                  </p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemove(item.id)}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`remove-item-${index}`}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </div>
                            ) : item.type === 'gift_card' ? (
                              /* Gift Card Item */
                              <div className="flex gap-4">
                                <div className="w-24 h-24 shrink-0 bg-gradient-to-br from-[var(--rose-gold)]/20 to-[var(--champagne)] flex items-center justify-center">
                                  <Gift size={32} className="text-[var(--gold)]" />
                                </div>
                                <div className="flex-1">
                                  <h3 className="font-['Cormorant_Garamond'] text-lg mb-2 text-[var(--charcoal)]">
                                    Carte Cadeau
                                    {item.gift_type === 'prestation' && item.prestation_name && (
                                      <span className="block text-sm font-normal text-[var(--charcoal)]/60">
                                        {item.prestation_name}
                                      </span>
                                    )}
                                  </h3>
                                  <div className="space-y-1 text-sm text-[var(--charcoal)]/70">
                                    <div>
                                      <span className="text-[var(--charcoal)]/50">Type:</span>{' '}
                                      {item.gift_type === 'preset_amount' && 'Montant prédéfini'}
                                      {item.gift_type === 'custom_amount' && 'Montant personnalisé'}
                                      {item.gift_type === 'prestation' && 'Prestation'}
                                    </div>
                                    {item.receiver_name && (
                                      <div>
                                        <span className="text-[var(--charcoal)]/50">Pour:</span>{' '}
                                        {item.receiver_name}
                                      </div>
                                    )}
                                    {item.sender_name && (
                                      <div>
                                        <span className="text-[var(--charcoal)]/50">De:</span>{' '}
                                        {item.sender_name}
                                      </div>
                                    )}
                                  </div>
                                  {item.personal_message && (
                                    <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic line-clamp-2">
                                      "{item.personal_message}"
                                    </p>
                                  )}
                                </div>
                                <div className="text-right">
                                  <p className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)] mb-4">
                                    {item.totalPrice}€
                                  </p>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleRemove(item.id)}
                                    className="text-red-500 hover:text-red-700"
                                    data-testid={`remove-item-${index}`}
                                  >
                                    <Trash2 size={14} />
                                  </Button>
                                </div>
                              </div>
                            ) : (
                              /* Press On Nails Item */
                              <div className="flex gap-4">
                                {/* Preview */}
                                <div className="w-24 h-24 shrink-0 bg-[var(--champagne)]/30 flex items-center justify-center">
                                  {item.photos?.[0] ? (
                                    <img
                                      src={item.photos[0].preview || item.photos[0].url}
                                      alt="Preview"
                                      className="w-full h-full object-cover"
                                    />
                                  ) : (
                                    <div
                                      className="w-12 h-12 rounded-full border-2 border-[var(--champagne)]"
                                      style={{ backgroundColor: item.color }}
                                    />
                                  )}
                                </div>

                                {/* Details */}
                                <div className="flex-1">
                                  <h3 className="font-['Cormorant_Garamond'] text-lg mb-2 text-[var(--charcoal)]">
                                    Press On Nails Personnalisés
                                  </h3>
                                  <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-sm text-[var(--charcoal)]/70">
                                    <div>
                                      <span className="text-[var(--charcoal)]/50">Forme:</span>{' '}
                                      {item.shapeLabel || shapeLabels[item.shape] || item.shape}
                                    </div>
                                    <div>
                                      <span className="text-[var(--charcoal)]/50">Longueur:</span>{' '}
                                      {item.lengthLabel || lengthLabels[item.length] || item.length}
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <span className="text-[var(--charcoal)]/50">Couleur:</span>
                                      <div
                                        className="w-4 h-4 rounded-full border border-gray-200"
                                        style={{ backgroundColor: item.color }}
                                      />
                                      <span>{item.colorLabel || ''}</span>
                                    </div>
                                    {item.nailArt && item.nailArt !== 'none' && (
                                      <div>
                                        <span className="text-[var(--charcoal)]/50">Nail Art:</span>{' '}
                                        {item.nailArtLabel || item.nailArt}
                                      </div>
                                    )}
                                  </div>
                                  {item.nailArtDescription && (
                                    <p className="mt-2 text-xs text-[var(--charcoal)]/50 italic">
                                      "{item.nailArtDescription}"
                                    </p>
                                  )}
                                </div>

                                {/* Price & Actions */}
                                <div className="text-right">
                                  <p className="font-['Cormorant_Garamond'] text-2xl text-[var(--gold)] mb-4">
                                    {item.totalPrice}€
                                  </p>
                                  <div className="flex gap-2 justify-end">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => navigate('/press-on-nails', { state: { editItem: item } })}
                                      className="text-[var(--charcoal)]/60"
                                      data-testid={`edit-item-${index}`}
                                    >
                                      <Edit2 size={14} />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleRemove(item.id)}
                                      className="text-red-500 hover:text-red-700"
                                      data-testid={`remove-item-${index}`}
                                    >
                                      <Trash2 size={14} />
                                    </Button>
                                  </div>
                                </div>
                              </div>
                            )}
                          </div>
                        </StaggerItem>
                      </motion.div>
                    ))}
                  </StaggerContainer>
                </AnimatePresence>
              </div>

              {/* Summary */}
              <div className="lg:col-span-1">
                <FadeIn>
                  <div className="bg-white p-6 border border-[var(--champagne)] sticky top-32">
                    <h3 className="font-['Cormorant_Garamond'] text-xl mb-6 text-[var(--charcoal)]">
                      Récapitulatif
                    </h3>

                    <div className="space-y-3 mb-4 pb-4 border-b border-[var(--champagne)]">
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--charcoal)]/60">Sous-total</span>
                        <span>{subtotal.toFixed(2)}€</span>
                      </div>
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--charcoal)]/60 flex items-center gap-1">
                          <Truck size={14} />
                          Livraison
                        </span>
                        <span className={isFreeShipping ? 'text-green-600 font-medium' : ''}>
                          {isFreeShipping ? 'Offerte' : `${deliveryFee.toFixed(2)}€`}
                        </span>
                      </div>
                    </div>
                    
                    {/* Free shipping progress */}
                    {!isFreeShipping && items.length > 0 && (
                      <div className="mb-4 p-3 bg-[var(--champagne)]/30">
                        <div className="flex items-center gap-2 text-sm text-[var(--charcoal)]/70 mb-2">
                          <Gift size={14} className="text-[var(--gold)]" />
                          <span>Plus que <strong>{amountForFreeShipping.toFixed(2)}€</strong> pour la livraison gratuite</span>
                        </div>
                        <div className="w-full bg-[var(--champagne)] h-2 rounded-full overflow-hidden">
                          <div 
                            className="bg-[var(--gold)] h-full transition-all duration-500"
                            style={{ width: `${Math.min((subtotal / FREE_SHIPPING_THRESHOLD) * 100, 100)}%` }}
                          />
                        </div>
                      </div>
                    )}
                    
                    {isFreeShipping && items.length > 0 && hasPressOnNails && (
                      <div className="mb-4 p-3 bg-green-50 border border-green-200 flex items-center gap-2">
                        <Gift size={16} className="text-green-600" />
                        <span className="text-sm text-green-700 font-medium">Livraison offerte !</span>
                      </div>
                    )}
                    
                    {isFreeShipping && items.length > 0 && !hasPressOnNails && (
                      <div className="mb-4 p-3 bg-blue-50 border border-blue-200 flex items-center gap-2">
                        <Gift size={16} className="text-blue-600" />
                        <span className="text-sm text-blue-700 font-medium">Livraison dématérialisée par email</span>
                      </div>
                    )}

                    <div className="flex justify-between items-center mb-6">
                      <span className="font-medium text-[var(--charcoal)]">Total</span>
                      <span className="font-['Cormorant_Garamond'] text-3xl text-[var(--gold)]">
                        {totalWithShipping.toFixed(2)}€
                      </span>
                    </div>

                    {!isAuthenticated && (
                      <div className="flex items-start gap-2 p-3 bg-[var(--champagne)]/30 mb-4 text-sm">
                        <AlertCircle size={16} className="text-[var(--gold)] shrink-0 mt-0.5" />
                        <p className="text-[var(--charcoal)]/70">
                          Vous devrez vous connecter pour finaliser votre commande.
                        </p>
                      </div>
                    )}

                    <Button
                      onClick={handleCheckout}
                      className="w-full py-6 bg-[var(--gold)] text-white text-xs uppercase tracking-[0.2em] font-medium hover:bg-[var(--dark-gold)] transition-colors rounded-none"
                      data-testid="btn-checkout"
                    >
                      Passer au paiement
                      <ArrowRight size={16} className="ml-2" />
                    </Button>

                    <p className="mt-4 text-xs text-center text-[var(--charcoal)]/50">
                      Paiement sécurisé • Livraison en 5-7 jours
                    </p>
                  </div>
                </FadeIn>

                {/* Continue Shopping */}
                <FadeIn delay={0.1}>
                  <Link
                    to="/press-on-nails"
                    className="block mt-4 text-center text-sm text-[var(--dark-rosegold)] hover:text-[var(--gold)] transition-colors"
                  >
                    ← Continuer mes achats
                  </Link>
                </FadeIn>
              </div>
            </div>
          )}
        </div>
      </section>
    </PageTransition>
  );
}
